/* ═══════════════════════════════════════════════════════════════
   ALTERLY — IndexedDB Local Project Storage
   Zero external dependencies, pure native browser storage.
   ═══════════════════════════════════════════════════════════════ */

import type { AnyAlterlyProjectSchema } from '../domain/serialization';

const DB_NAME = 'alterly_studio_db';
const DB_VERSION = 1;
const STORE_NAME = 'projects';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      return reject(new Error('IndexedDB is not available in this environment.'));
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Saves a project document to IndexedDB.
 */
export async function saveProjectToDb(project: AnyAlterlyProjectSchema): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(project);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

/**
 * Loads a project document by ID from IndexedDB.
 */
export async function loadProjectFromDb(id: string): Promise<AnyAlterlyProjectSchema | null> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

/**
 * Loads the most recently saved project (last autosave) from IndexedDB.
 */
export async function loadLatestProjectFromDb(): Promise<AnyAlterlyProjectSchema | null> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const items = (request.result as AnyAlterlyProjectSchema[]) || [];
      if (items.length === 0) {
        resolve(null);
        return;
      }
      // Sort descending by modifiedAt
      items.sort((a, b) => new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime());
      resolve(items[0]);
    };
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}
